// src/app/service-form/service-form.component.ts

import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ServiceService } from '../service-list.service';
import { finalize } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';

// COMMENT: NEW IMPORTS: Import the GstSlabService and its required interfaces.
import { GstSlabService, GstSlab } from '../../gstslabs/gst-slab.service';
import { HttpErrorResponse } from '@angular/common/http'; // For better error handling

// Interface definitions (as requested not to use external models)
interface Department {
  id: string;
  name: string;
}

// COMMENT: HSNCode interface is REMOVED as it is no longer a list of objects.

// COMMENT: UPDATED INTERFACE: Add hsnCode and gstSlabId fields.
interface ServicePayload {
  name: string;
  description: string;
  departmentId: string | null; // Use null for the initial "Select Department" value
  price: number | null;
  isActive: boolean;
  hsnCode: string | null; // HSN code is now a single string value
  gstRate: Number | null;
  serviceOwner: string | null;
}

// Initial state for a new service
// COMMENT: UPDATED initialServicePayload: Set defaults for new fields.
const initialServicePayload: ServicePayload = {
  name: '',
  description: '',
  departmentId: null,
  price: null,
  isActive: true,
  hsnCode: null,
  gstRate: null,
  serviceOwner: null,
};

@Component({
  selector: 'app-service-form',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './create-service.component.html',
})
export class CreateServiceComponent implements OnInit {
  serviceId: string | null = null;
  viewOnly: boolean = false;

  servicePayload: ServicePayload = { ...initialServicePayload };

  departments: Department[] = [];
  // COMMENT: UPDATED TYPE: Use the imported GstSlab interface.
  gstSlabs: GstSlab[] = [];
  // hsnCodes: HsnCode[] = []; // REMOVED - not needed for generating a single code

  isLoading = false;
  isSubmitting = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private serviceService: ServiceService,
    // COMMENT: NEW INJECTION: Inject GstSlabService.
    private gstSlabService: GstSlabService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    // 1. Get route parameters
    this.serviceId = this.route.snapshot.paramMap.get('id');
    this.viewOnly = this.route.snapshot.queryParamMap.has('view');

    // 2. Load dependencies (Departments, GST Slabs)
    this.loadDependencies();

    // 3. Load existing service data if ID is present
    if (this.serviceId) {
      this.loadServiceData(this.serviceId);
    }
  }

  /**
   * Fetches the list of all departments and GST slabs.
   */
  loadDependencies(): void {
    // Load Departments
    this.serviceService.getDepartments(1, 100).subscribe({
      next: (response: any) => {
        this.departments = response || [];
      },
      error: (err: HttpErrorResponse) => {
        console.error('Failed to load departments:', err);
        this.toastr.error('Failed to load departments.', 'Error');
      },
    });

    // Load GST Slabs (Fetching all on the assumption there aren't too many)
    this.gstSlabService.getAllGstSlabs(1, 100).subscribe({
      next: (response) => {
        this.gstSlabs = response.data || [];
      },
      error: (err: HttpErrorResponse) => {
        console.error('Failed to load GST Slabs:', err);
        this.toastr.error('Failed to load GST Slabs.', 'Error');
      },
    });
  }

  /**
   * Fetches the service data for editing or viewing.
   * @param id The ID of the service.
   */
  loadServiceData(id: string): void {
    this.isLoading = true;
    this.serviceService
      .getServiceById(id)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (service: any) => {
          // Map API response to servicePayload, ensuring price is a number/string
          this.servicePayload = {
            name: service.name,
            description: service.description,
            departmentId: service.departmentId || null,
            // Ensure price is handled correctly (Number or null)
            price: service.price ? Number(service.price) : null,
            isActive: service.isActive || false,
            hsnCode: service.hsnCode || null,
            gstRate: service.gstRate || null,
            serviceOwner: service.serviceOwner || null,
          };
        },
        error: (err: HttpErrorResponse) => {
          console.error('Failed to load service:', err);
          this.toastr.error('Failed to load service data.', 'Error');
          // Navigate back or show error
          this.router.navigate(['/service']);
        },
      });
  }

  /**
   * Resets the form to its initial state for creation or original state for editing.
   */
  resetForm(): void {
    if (this.serviceId) {
      // Re-load original data for existing service
      this.loadServiceData(this.serviceId);
    } else {
      // Reset to initial state for new service
      this.servicePayload = { ...initialServicePayload };
    }
  }

  /**
   * Handles form submission (Create or Update).
   */
  submitService(): void {
    if (this.isSubmitting) return;

    // Basic validation check
    if (
      !this.servicePayload.name ||
      !this.servicePayload.departmentId ||
      this.servicePayload.price === null ||
      this.servicePayload.gstRate === null // Check for null/undefined rate
    ) {
      this.toastr.warning(
        'Please fill in all required fields: Service Name, Department, Price, HSN Code, and GST Rate.',
        'Missing Fields'
      );
      return;
    }

    this.isSubmitting = true;

    // FIX: Create the final payload and convert gstRate to Number
    const payloadToSend: ServicePayload = {
      ...this.servicePayload,
      // Ensure gstRate is sent as a number.
      // parseFloat handles strings bound from inputs.
      gstRate: parseFloat(String(this.servicePayload.gstRate)),
    };

    let serviceOperation: Observable<any>;
    const successMessage = this.serviceId
      ? `Service "${payloadToSend.name}" updated successfully!`
      : `Service "${payloadToSend.name}" created successfully!`;

    if (this.serviceId) {
      // Update existing service
      serviceOperation = this.serviceService.updateService(
        this.serviceId,
        payloadToSend // Use the converted payload
      );
    } else {
      // Create new service
      serviceOperation = this.serviceService.createService(payloadToSend); // Use the converted payload
    }

    serviceOperation
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          this.toastr.success(successMessage, 'Success');

          // 2. Navigate to the /service list page
          this.router.navigate(['/services']);
        },
        error: (err) => {
          console.error(`Service submission failed:`, err);
          this.toastr.error(
            'Failed to save service. Please try again.',
            'Error'
          );
        },
      });
  }
}
